import React, { useMemo } from 'react';
import {
    Button,
    Card,
    Space,
    Table,
    Tag,
    Typography,
    Alert,
    Spin,
    DatePicker,
    Progress,
    List,
} from 'antd';
import dayjs from 'dayjs';
import type { Client } from '@stomp/stompjs';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '@/modules/auth/stores/auth-store';
import { useHasPermission } from '@/modules/auth/hooks/use-has-permission';
import { createDayProcessClient } from './day-process-websocket';
import { useTriggerSod, useTriggerEod, useRunHistory, useSessionSummary } from './useDayEnd';
import { DayEndService } from './DayEndService';
import type { DayEndRun, DayProcessProgressEvent } from './DayEndService';

const { Title, Text } = Typography;

const statusColor: Record<string, string> = {
    DONE: 'green',
    RUNNING: 'blue',
    FAILED: 'red',
};

const columns = [
    { title: 'Date', dataIndex: 'sessionDate', key: 'sessionDate' },
    { title: 'Type', dataIndex: 'runType', key: 'runType' },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => <Tag color={statusColor[status] ?? 'default'}>{status}</Tag>,
    },
    { title: 'Started By', dataIndex: 'startedBy', key: 'startedBy' },
    {
        title: 'Duration',
        key: 'duration',
        render: (_: unknown, record: DayEndRun) => {
            if (!record.completedAt) return record.status === 'RUNNING' ? 'In progress…' : '—';
            const ms = dayjs(record.completedAt).diff(dayjs(record.startedAt));
            return `${Math.round(ms / 1000)}s`;
        },
    },
    {
        title: 'Error',
        dataIndex: 'errorMessage',
        key: 'errorMessage',
        render: (msg: string | null) => msg ? <Text type="danger">{msg}</Text> : '—',
    },
];

const DayEndPage: React.FC = () => {
    const { branch: urlBranch } = useParams<{ branch: string }>();
    const sessionBranch = useAuthStore((s) => s.user?.branch ?? '');
    const refreshUser = useAuthStore((s) => s.refreshUser);
    const branch = urlBranch || sessionBranch;
    const canStartSession = useHasPermission('00810');
    const canEndSession = useHasPermission('00810');
    const [targetDate, setTargetDate] = React.useState<string | null>(null);
    const [progressEvents, setProgressEvents] = React.useState<DayProcessProgressEvent[]>([]);
    const clientRef = React.useRef<Client | null>(null);

    const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useSessionSummary(branch);
    const { data: history, isLoading: historyLoading, refetch: refetchHistory } = useRunHistory(branch);

    React.useEffect(() => {
        let active = true;

        // Fetch current running operation on mount
        const loadCurrentRunning = async () => {
            try {
                const event = await DayEndService.getCurrentRunning(branch);
                if (active && event) {
                    setProgressEvents((current) => [event, ...current].slice(0, 20));
                }
            } catch (err) {
                console.error('Failed to load current running operation:', err);
            }
        };

        loadCurrentRunning();

        createDayProcessClient(async (event) => {
            setProgressEvents((current) => [event, ...current].slice(0, 20));
            if (event.completed) {
                await Promise.allSettled([refetchHistory(), refetchSummary(), refreshUser()]);
            }
        }).then((client) => {
            if (!active) {
                client.deactivate();
                return;
            }
            clientRef.current = client;
        });
        return () => {
            active = false;
            clientRef.current?.deactivate();
            clientRef.current = null;
        };
    }, [refetchHistory, refetchSummary, refreshUser, branch]);

    const { trigger: triggerSod, loading: sodLoading, result: sodResult } = useTriggerSod(branch);
    const { trigger: triggerEod, loading: eodLoading, result: eodResult } = useTriggerEod(branch);
    const latestProgress = progressEvents[0];
    const progressPercent = latestProgress?.completedBranches && latestProgress?.totalBranches
        ? Math.round((latestProgress.completedBranches / latestProgress.totalBranches) * 100)
        : 0;

    const handleSod = async () => {
        await triggerSod(targetDate);
        refetchHistory();
        refetchSummary();
        await refreshUser();
    };

    const handleEod = async () => {
        await triggerEod();
        refetchHistory();
        refetchSummary();
        await refreshUser();
    };

    return (
        <div style={{ padding: 24 }}>
            <Title level={3}>Day-End Processing — {branch}</Title>

            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Control panel */}
                <Card title="Session Summary" loading={summaryLoading}>
                    <Space direction="vertical" size="small">
                        <Text>Current session date: <strong>{summary?.currentSessionDate ?? '—'}</strong></Text>
                        <Text>Next session date: <strong>{summary?.nextSessionDate ?? '—'}</strong></Text>
                        <Text>
                            Session status:{' '}
                            <Tag color={summary?.sessionStatus === 'O' ? 'green' : 'red'}>
                                {summary?.sessionStatus === 'O' ? 'OPEN' : summary?.sessionStatus === 'C' ? 'CLOSED' : '—'}
                            </Tag>
                        </Text>
                    </Space>
                </Card>

                {(canStartSession || canEndSession) && (
                    <Card title="Run Controls">
                        <Space size="middle" wrap>
                            <DatePicker
                                value={targetDate ? dayjs(targetDate) : null}
                                onChange={(value) => setTargetDate(value ? value.format('YYYY-MM-DD') : null)}
                                disabled={sodLoading || eodLoading || !canStartSession}
                                placeholder="Optional future start date"
                                allowClear
                            />
                            {canStartSession && (
                                <Button
                                    type="primary"
                                    loading={sodLoading}
                                    disabled={!branch || summary?.sessionStatus === 'O'}
                                    onClick={handleSod}
                                >
                                    {targetDate ? 'Run Start-Day To Target Date' : 'Run Start-of-Day (SOD)'}
                                </Button>
                            )}

                            {canEndSession && (
                                <Button
                                    type="primary"
                                    danger
                                    loading={eodLoading}
                                    disabled={!branch || summary?.sessionStatus !== 'O'}
                                    onClick={handleEod}
                                >
                                    Run End-of-Day (EOD)
                                </Button>
                            )}
                        </Space>

                        {canStartSession && summary?.sessionStatus !== 'O' && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">Use Start-of-Day to open all branches. Pick a future date to run sequential EOD/SOD cycles until that date is reached.</Text>
                            </div>
                        )}
                    </Card>
                )}

                <Card title="Live Progress">
                    {latestProgress ? (
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>{latestProgress.message}</Text>
                            <Text type="secondary">
                                {latestProgress.operation} · {latestProgress.status}
                                {latestProgress.branch ? ` · ${latestProgress.branch}` : ''}
                                {latestProgress.cycle && latestProgress.totalCycles ? ` · cycle ${latestProgress.cycle}/${latestProgress.totalCycles}` : ''}
                            </Text>
                            <Progress percent={progressPercent} status={latestProgress.status === 'FAILED' ? 'exception' : latestProgress.completed ? 'success' : 'active'} />
                            {latestProgress.forceLogout && <Alert type="warning" message="All other active users were logged out before end-of-day processing." showIcon />}
                            <List
                                size="small"
                                bordered
                                dataSource={progressEvents}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Text>{item.message}</Text>
                                    </List.Item>
                                )}
                            />
                        </Space>
                    ) : (
                        <Text type="secondary">No live progress yet.</Text>
                    )}
                </Card>

                <Card title="Branch Session States" loading={summaryLoading}>
                    <Table
                        dataSource={summary?.branches ?? []}
                        rowKey="branch"
                        size="small"
                        pagination={false}
                        columns={[
                            { title: 'Branch', dataIndex: 'branch', key: 'branch' },
                            { title: 'Name', dataIndex: 'branchName', key: 'branchName' },
                            { title: 'Session Date', dataIndex: 'sessionDate', key: 'sessionDate' },
                            {
                                title: 'Status',
                                dataIndex: 'sessionStatus',
                                key: 'sessionStatus',
                                render: (value: string) => <Tag color={value === 'O' ? 'green' : 'red'}>{value === 'O' ? 'OPEN' : 'CLOSED'}</Tag>,
                            },
                        ]}
                    />
                </Card>

                {/* SOD result */}
                {sodResult && (
                    <Alert
                        type={sodResult.success ? 'success' : 'error'}
                        message={sodResult.success ? 'SOD completed' : 'SOD failed'}
                        description={
                            sodResult.success
                                ? `Steps: ${sodResult.stepsCompleted.join(' → ')}`
                                : sodResult.error ?? 'Unknown error'
                        }
                    />
                )}

                {/* EOD result */}
                {eodResult && (
                    <Alert
                        type={eodResult.success ? 'success' : 'error'}
                        message={eodResult.success ? 'EOD completed' : 'EOD failed'}
                        description={
                            eodResult.success
                                ? `Steps: ${eodResult.stepsCompleted.join(' → ')}`
                                : eodResult.error ?? 'Unknown error'
                        }
                    />
                )}

                {/* History table */}
                <Card title="Run History (last 30)">
                    <Spin spinning={historyLoading}>
                        <Table<DayEndRun>
                            dataSource={history ?? []}
                            columns={columns}
                            rowKey="id"
                            size="small"
                            pagination={false}
                        />
                    </Spin>
                </Card>
            </Space>
        </div>
    );
};

export default DayEndPage;
